import {
  Button,
  DateInput,
  Form,
  Grid,
  Input,
  Select,
  Stack,
  Text,
  useBasisForm,
} from "basis";
import { FormActions } from "components/FormActions/FormActions";
import { Layout } from "components/Layout/Layout";
import { Separator } from "components/Separator/Separator";
import { validateEmail } from "library/validation/customInputValidation";
import { employmentStatusOptions } from "pages/HardshipApplication/options";
import { useHardshipApplicationContext } from "../hooks/useHardshipApplicationContext";
import { useNav } from "../hooks/useNav";
import { getNumberOfSteps } from "../reducer/hardshipSelectors";
import { ActionTypes, PersonalDetailsState } from "../reducer/types";
import { AddressField } from "./AddressField/AddressField";

export const PersonalDetails = () => {
  const { state, dispatch } = useHardshipApplicationContext();

  const { personalDetails, prescreening } = state;
  const { methods, Field } = useBasisForm<PersonalDetailsState>({
    defaultValues: personalDetails,
  });

  const { navigate, goBack } = useNav();

  // usePageTracking({
  //   page: {
  //     pageType: "input-page",
  //     siteSection: "hardship",
  //     siteSubSection: "personal-details",
  //     pageTitle: "Personal details",
  //   },
  //   user: {
  //     authenticationStatus: "not logged in",
  //   },
  // });

  if (!prescreening) {
    return null;
  }

  const onSubmit = (values: PersonalDetailsState) => {
    dispatch({ type: ActionTypes.COMPLETE_PERSONAL_DETAILS, ...values });
    navigate("accounts");
  };

  const numberOfSteps = getNumberOfSteps(state);

  return (
    <Layout>
      <Layout.Header>Hardship Care application</Layout.Header>
      <Layout.Main>
        <Form methods={methods} onSubmit={onSubmit}>
          <Stack gap="8" width="100%" width-sm="548">
            <Stack gap="4">
              <Text textStyle="body1">
                <strong>Step 1 of {numberOfSteps}</strong>
              </Text>
              <Text as="h2" textStyle="heading4">
                Personal details
              </Text>
            </Stack>
            <Separator />

            {prescreening.applyingOnBehalfOfOther === "yes_on_behalf" && (
              <>
                <Stack gap="8">
                  <Text as="h3" textStyle="heading5" color="primary.blue.t100">
                    Authorised person's details
                  </Text>
                  <Grid cols={1} cols-sm={2} colsGap-sm="4" rowsGap="8">
                    <Grid.Item colSpan="all" colSpan-sm="0" rowSpan-sm="0">
                      <Field
                        label="Full name"
                        name="authorisedPersonDetails.fullName"
                        as={Input}
                      />
                    </Grid.Item>
                    <Grid.Item colSpan="all" rowSpan-sm="1">
                      <Field
                        label="Organisation name"
                        name="authorisedPersonDetails.counsellorName"
                        as={Input}
                      />
                    </Grid.Item>
                    <Grid.Item colSpan="all" colSpan-sm="0" rowSpan-sm="2">
                      <Field
                        label="Email address"
                        name="authorisedPersonDetails.email"
                        validate={validateEmail}
                        as={Input}
                      />
                    </Grid.Item>
                    <Grid.Item colSpan="all" colSpan-sm="1" rowSpan-sm="2">
                      <Field
                        label="Contact number"
                        name="authorisedPersonDetails.contactNumber"
                        variant="numeric"
                        type="tel"
                        as={Input}
                      />
                    </Grid.Item>
                  </Grid>
                  <Separator />
                </Stack>
              </>
            )}
            <Text as="h3" textStyle="heading5" color="primary.blue.t100">
              Applicant details
            </Text>
            <Grid cols={1} cols-sm={2} colsGap-sm="4" rowsGap="8">
              <Grid.Item colSpan="all" colSpan-sm="0" rowSpan-sm="0">
                <Field
                  label="First name"
                  name="applicantDetails.firstName"
                  as={Input}
                />
              </Grid.Item>
              <Grid.Item colSpan="all" colSpan-sm="1" rowSpan-sm="0">
                <Field
                  label="Last name"
                  name="applicantDetails.lastName"
                  as={Input}
                />
              </Grid.Item>
              <Grid.Item colSpan="all" colSpan-sm="0" rowSpan-sm="1">
                <Field
                  label="Date of birth"
                  name="applicantDetails.dateOfBirth"
                  as={DateInput}
                />
              </Grid.Item>
              <Grid.Item colSpan="all" colSpan-sm="0" rowSpan-sm="2">
                <Field
                  label="Contact number"
                  name="applicantDetails.contactNumber"
                  variant="numeric"
                  type="tel"
                  as={Input}
                />
              </Grid.Item>
              <Grid.Item colSpan="all" colSpan-sm="1" rowSpan-sm="2">
                <Field
                  label="Email address"
                  name="applicantDetails.email"
                  validate={validateEmail}
                  as={Input}
                />
              </Grid.Item>
              <Grid.Item colSpan="all" rowSpan-sm="3">
                <Stack gap="4">
                  <Separator />
                  <AddressField />
                  <Separator />
                </Stack>
              </Grid.Item>
              <Grid.Item colSpan="all" colSpan-sm="0" rowSpan-sm="4">
                <Field
                  label="Employment status"
                  name="applicantDetails.employmentStatus"
                  options={employmentStatusOptions}
                  as={Select}
                />
              </Grid.Item>
            </Grid>
            <FormActions>
              <FormActions.Primary>
                <Button variant="primary" type="submit">
                  Next
                </Button>
              </FormActions.Primary>
              <FormActions.Secondary>
                <Button variant="secondary" onClick={goBack}>
                  Back
                </Button>
              </FormActions.Secondary>
            </FormActions>
          </Stack>
        </Form>
      </Layout.Main>
    </Layout>
  );
};
